import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MarkdownModule } from 'ngx-markdown';
import { TicketLog } from '../../../core/models/ticket.model';
import { constants } from '../../../../../shared/utils/constants';

/**
 * Lista o histórico de apontamentos de um chamado. Só desenha: não busca dados
 * nem encerra nada — emite `stopRequested` e quem hospeda decide.
 *
 * Usado pela aba "Histórico" do `time-entry-modal` e pelo modal de leitura da
 * tela `/chamados/apontamentos/historico`.
 *
 * Encapsulation é o padrão (Emulated) mesmo o componente de origem sendo
 * `None`: as classes daqui incluem `.empty-state`, que outras telas também
 * usam — globais, elas vazariam para lá. Nenhuma regra mira o HTML gerado pelo
 * `<markdown>` (os wrappers só definem propriedades herdadas), então escopar
 * não muda nada visualmente.
 */
@Component({
  selector: 'app-time-entry-history',
  standalone: true,
  imports: [ButtonModule, TooltipModule, MarkdownModule],
  templateUrl: './time-entry-history.component.html',
  styleUrls: ['./time-entry-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeEntryHistoryComponent {
  @Input() entries: TicketLog[] = [];
  @Input() activeEntry: TicketLog | null = null;

  /** Desabilita (não esconde) o botão de encerrar: a ação existe, só não é aqui. */
  @Input() readOnly = false;

  @Output() stopRequested = new EventEmitter<void>();

  private readonly timeTypes = constants.ticketcenter.ticketLogTypes;
  private readonly attendanceTypes = constants.ticketcenter.ticketLogAttendanceTypes;
  private readonly departmentTypes = constants.ticketcenter.ticketLogDepartmentTypes;

  formatDate(dateString: string): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  }

  calculateDurationLog(entry: TicketLog): string {
    if (!entry.start_date) return '';

    const start = new Date(entry.start_date);
    const end = entry.end_date ? new Date(entry.end_date) : new Date();
    const diffMs = end.getTime() - start.getTime();

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}min`;
    } else {
      return `${minutes}min`;
    }
  }

  /**
   * O `type` chega ora como código (`M`, `E`, `I`, `A`), ora como rótulo já
   * traduzido, dependendo do endpoint que montou a linha — daí o fallback para
   * o próprio valor em vez de string vazia.
   */
  getTypeLabel(value: string | null | undefined): string {
    return this.timeTypes.find((t: any) => t.value === value)?.label ?? value ?? '';
  }

  getAttendanceLabel(value: string | null | undefined): string {
    return this.attendanceTypes.find((t: any) => t.value === value)?.label ?? value ?? '';
  }

  getDepartmentLabel(value: string | null | undefined): string {
    return this.departmentTypes.find((t: any) => t.value === value)?.label ?? value ?? '';
  }
}
